import type { ServiceItem } from '../types';
import { hatBestandteile } from './bestandteile/bestandteilLogic';
import type { ListProjectServiceRow } from './listProject';

/**
 * Der Spiegel der eingebetteten `project_services` einer Listenzeile — aus
 * den Positionen des offenen Projekts, IM SPEICHER.
 *
 * Nach jedem Speichern bzw. Flush ersetzt die App damit `project_services` im
 * Listeneintrag (App.handleUpdateReport, App.handleQueueFlushed), damit die
 * Listenansichten sofort den neuen Stand sehen. `getNormalizedServices` liest
 * diese Zeilen VOR dem Blob: jede Spalte, die hier fehlt, ist in der Liste bis
 * zum naechsten echten Laden weg. Der Rueckgabetyp `ListProjectServiceRow`
 * macht eine fehlende Spalte zum Uebersetzungsfehler — so ist der Verlust von
 * Verzicht, Pauschale und Herkunft aufgefallen.
 *
 * Geht NIE in die Datenbank — gespeichert wird ueber saveProjectToCloud.
 * Bis 21.10.2026 lag die Funktion in App.tsx.
 */
export const mapServicesToDb = (services: ServiceItem[]): ListProjectServiceRow[] => {
    return services.map(s => ({
        id: s.id,
        lv_position: s.lvPosition,
        title: s.title,
        unit_price: s.unitPrice,
        quantity_contracted: s.quantityContracted,
        quantity_used: s.quantityUsed,
        quantity_billed: s.quantityBilled,
        assignment: s.assignment,
        unit: s.unit,
        status: s.status,
        category: s.category,
        client_lv_position: s.clientLvPosition,
        // NEP aus demselben Grund wie min_hours darunter: Dashboard und
        // Aufgabenliste lesen is_optional aus der Listenzeile. Ohne die
        // Spalte im Spiegel forderte das Dashboard eine Eventualposition
        // nach dem ersten Speichern als offenes Dokument ein.
        is_optional: s.is_optional === true,
        // Muss mit: dieser Spiegel ersetzt project_services in der
        // zwischengespeicherten Listenzeile, und getNormalizedServices liest
        // die relationale Quelle VOR dem JSON-Blob. Ohne die Spalte verloere
        // die Position ihre Mindeststunden bis zum naechsten echten Laden.
        min_hours: s.minHours ?? null,
        // Paketposition (Plan Bestandteile, Befund A3): Definition UND Gate
        // muessen in den Spiegel, sonst saehen Listenansichten (Cockpit
        // „noch abzurechnen", getNormalizedServices auf der Listenzeile)
        // eine Paketposition bis zum naechsten Laden OHNE Gate — fail OPEN.
        // `undefined` bleibt `undefined` (Spalte nicht geladen, wie dort);
        // `bestandteile_offen` ist nur der gelesene Wert der Datenbank:
        // dieser Spiegel geht NIE in die Datenbank (Speichern laeuft ueber
        // saveProjectToCloud, das die Spalte herausnimmt).
        bestandteile: s.bestandteile === undefined ? undefined : (hatBestandteile(s) ? s.bestandteile : null),
        bestandteile_offen: s.bestandteileOffen ?? null,
        // Stundenkontingent (Nr. 171) aus demselben Grund: die Listenzeile
        // wird ueber projectLv.bestandteilFelderAusZeile wieder gelesen, ohne
        // die Spalten im Spiegel verloere die Paketposition ihre Stunden bis
        // zum naechsten echten Laden. `undefined` bleibt `undefined` (nicht
        // gelesen); `stunden_erfasst` ist nur der gelesene Wert der
        // Datenbank — auch er geht von hier NIE in die Datenbank.
        stunden_kontingent: s.stundenKontingent,
        stunden_erfasst: s.stundenErfasst,
        // VERZICHT (Nr. 70), MONATSPAUSCHALE (Nr. 53) und HERKUNFT (Nr. 68)
        // aus demselben Grund wie min_hours oben: getNormalizedServices liest
        // diese Zeilen VOR dem Blob. Fehlten die Spalten im Spiegel, stuenden
        // nach jedem Speichern verzichtete und pauschal gedeckte Mengen in
        // den Listenansichten (offene Positionen, Abrechnung) bis zum
        // naechsten echten Laden wieder als offenes Geld. Aufgefallen beim
        // Typisieren der Liste (listProject.ts). `null` statt `undefined`:
        // der Ladeweg liest `?? undefined` zurueck, die Position sieht also
        // aus wie frisch geladen. Auch dieser Teil geht NIE in die Datenbank.
        quantity_waived: s.quantityWaived ?? null,
        waived_reason: s.waivedReason ?? null,
        waived_at: s.waivedAt ?? null,
        waived_by: s.waivedBy ?? null,
        waived_entry_ids: s.waivedEntryIds ?? null,
        flat_rate: s.flatRate ?? null,
        transferred_from: s.transferredFrom ?? null,
    }));
};
